// 🔹 React Core
import React, { FC, RefObject, useEffect } from 'react';

// 🔹 UI Component Libraries
import { Checkbox, Modal } from 'antd';

// 🔹 Internal Components
import { ProgramWeek } from './ProgramWeek';
import MiniPTAi from 'src/components/MiniPTAi/MiniPTAi.component';
import type { WorkoutProgramParams } from 'src/api/mutations/useWorkoutProgram';

// 🔹 Custom Hooks
import { useProgramStore } from './useProgramStore';

// 🔹 Store
import { trainingStore } from 'src/store/trainingStore';
import { screenTimeTracker, TRAINER_SECTIONS } from 'src/utils/screenTimeTracker';

// 🔹 Assets
import oliverBennett from 'url:src/assets/images/oliver-bennett.png';
import miniPtCare from 'url:src/assets/videos/mini-pt-care.mp4';

interface ProgramBuilderProps {
  programBuilderRef: RefObject<HTMLDivElement | null>;
  store: ReturnType<typeof useProgramStore>;
  onOpenLibrary?: () => void;
  apiProgramData?: any;
  payload?: WorkoutProgramParams;
  onRemixWorkout: () => void;
  isRemixing: boolean;
  onAIModifyWorkout?: (option: string) => void;
  isAIModifying?: boolean;
  selectedAIOption?: string;
  aiModifySuccess?: boolean;
  setAiModifySuccess?: (success: boolean) => void;
}

const getAIText = (option: string) => {
  switch (option) {
    case 'Gradually raise workout intensity':
      return 'I’m working on gradually increasing the workout intensity of this program';
    case 'Adjust for gym equipment':
      return 'I’m working on adjusting for gym equipment for this program';
    case 'Progressively increase weights or reps':
      return 'I’m working on progressively increasing weights or reps for this program';
    case 'Convert to superset format':
      return 'I’m working on converting this program to a superset format';
    default:
      return `I'm working on ${option.toLowerCase()} for this program`;
  }
};

export const ProgramBuilder: FC<ProgramBuilderProps> = ({
  programBuilderRef,
  store,
  onOpenLibrary,
  apiProgramData,
  payload,
  onRemixWorkout,
  isRemixing,
  onAIModifyWorkout,
  isAIModifying,
  selectedAIOption = '',
  aiModifySuccess,
  setAiModifySuccess,
}) => {
  const {
    allWeeksSame,
    setAllWeeksSame,
    totalExercisesPlaced,
    weeks,
    isRemixModalOpen,
    setRemixModalOpen,
    isAiAssistantOpen,
    setAiAssistantOpen,
  } = store;

  // ── Screen time tracking – AI Chat ("How can I assist you today?") ───────
  // Only tracked when the initial options list is visible (not while AI is modifying or showing success)
  const _isAIChatVisible = isAiAssistantOpen && !isAIModifying && !aiModifySuccess;
  useEffect(() => {
    if (_isAIChatVisible) {
      screenTimeTracker.startSection(TRAINER_SECTIONS.AI_CHAT);
    } else {
      screenTimeTracker.stopSection(TRAINER_SECTIONS.AI_CHAT);
    }
    return () => {
      screenTimeTracker.stopSection(TRAINER_SECTIONS.AI_CHAT);
    };
  }, [_isAIChatVisible]);

  // Store
  const { assessment } = trainingStore();

  const programDurationText = apiProgramData
    ? `${apiProgramData.duration || 1} weeks · ${apiProgramData.datetimeList?.length || 0} days`
    : '8 weeks · 2 days';

  const programGoal =
    payload?.goals ||
    apiProgramData?.programmeName ||
    apiProgramData?.workoutType ||
    'Build Muscle';
  const experienceLevel = payload?.fitness_level || 'Intermediate';
  const injuries = payload?.injured_body_parts || 'None';

  return (
    <main className="program-builder" ref={programBuilderRef}>
      <div id="builder-header-profile" className="builder-header-profile">
        <div className="profile-info-section">
          <div className="profile-avatar">
            <img
              src={assessment?.selectedClient?.avatar || oliverBennett}
              alt={assessment?.selectedClient?.name || 'Oliver Bennett'}
            />
          </div>
          <div className="profile-name">
            <h3>{assessment?.selectedClient?.name || 'Oliver Bennett'}</h3>
            <p>{programDurationText}</p>
          </div>
        </div>

        <div className="profile-stats">
          <div className="stat">
            <label>Experience Level:</label>
            <span>
              <span style={{ marginRight: 8 }}>{experienceLevel}</span>
              <div className="experience-dots">
                <span className="dot filled"></span>
                <span className="dot filled"></span>
                <span className="dot filled"></span>
                <span className="dot empty"></span>
                <span className="dot empty"></span>
              </div>
            </span>
          </div>
          <div className="stat">
            <label>Program Goal:</label>
            <span>{programGoal}</span>
          </div>
          <div className="stat">
            <label>Pain/Injuries</label>
            <span style={{ textTransform: 'capitalize' }}>{injuries}</span>
          </div>
        </div>
      </div>

      <div className="builder-actions-row">
        <p className="getting-started-text">
          <strong>Getting started:</strong>{' '}
          <span className="text-light">
            Drag exercises from the library on the left into any day slot below &mdash; or use the
            AI panel to generate a complete plan instantly.
          </span>
        </p>
        <div className="action-buttons" id="builder-actions-row">
          <button
            id="remix-btn"
            className="remix-btn"
            onClick={onRemixWorkout}
            disabled={isRemixing || !payload}
          >
            {isRemixing ? 'Regenerating...' : 'Regenerate Workout'}
          </button>
          <button id="ai-btn" className="ai-btn" onClick={() => setAiAssistantOpen(true)}>
            <svg
              style={{ position: 'relative', top: '2px' }}
              width={16}
              height={16}
              viewBox="0 0 16 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M6.0714 3.63216C6.47007 2.4655 8.08207 2.43016 8.55474 3.52616L8.59474 3.63283L9.13274 5.20616C9.25603 5.56699 9.45527 5.89717 9.71702 6.17444C9.97877 6.45172 10.2969 6.66964 10.6501 6.8135L10.7947 6.8675L12.3681 7.40483C13.5347 7.8035 13.5701 9.4155 12.4747 9.88816L12.3681 9.92816L10.7947 10.4662C10.4338 10.5894 10.1035 10.7886 9.82608 11.0503C9.54869 11.3121 9.33067 11.6303 9.18674 11.9835L9.13274 12.1275L8.5954 13.7015C8.19674 14.8682 6.58474 14.9035 6.11274 13.8082L6.0714 13.7015L5.53407 12.1282C5.41086 11.7672 5.21165 11.4369 4.9499 11.1595C4.68814 10.8821 4.36993 10.6641 4.01674 10.5202L3.87274 10.4662L2.2994 9.92883C1.13207 9.53016 1.09674 7.91816 2.19274 7.44616L2.2994 7.40483L3.87274 6.8675C4.23356 6.7442 4.56374 6.54496 4.84102 6.28321C5.11829 6.02146 5.33621 5.70329 5.48007 5.35016L5.53407 5.20616L6.0714 3.63216ZM12.6667 1.3335C12.7915 1.3335 12.9137 1.36848 13.0195 1.43448C13.1253 1.50047 13.2105 1.59483 13.2654 1.70683L13.2974 1.78483L13.5307 2.46883L14.2154 2.70216C14.3404 2.74463 14.45 2.82324 14.5302 2.92804C14.6105 3.03284 14.6578 3.15911 14.6663 3.29084C14.6747 3.42258 14.6438 3.55385 14.5775 3.66802C14.5113 3.78219 14.4126 3.87412 14.2941 3.93216L14.2154 3.96416L13.5314 4.1975L13.2981 4.88216C13.2555 5.00712 13.1769 5.11663 13.072 5.19682C12.9672 5.27702 12.8409 5.32429 12.7092 5.33264C12.5774 5.341 12.4462 5.31006 12.3321 5.24375C12.2179 5.17744 12.1261 5.07874 12.0681 4.96016L12.0361 4.88216L11.8027 4.19816L11.1181 3.96483C10.9931 3.92237 10.8835 3.84375 10.8033 3.73895C10.723 3.63415 10.6756 3.50788 10.6672 3.37615C10.6588 3.24441 10.6897 3.11314 10.7559 2.99897C10.8222 2.8848 10.9208 2.79287 11.0394 2.73483L11.1181 2.70283L11.8021 2.4695L12.0354 1.78483C12.0804 1.65311 12.1654 1.53877 12.2786 1.45783C12.3918 1.37689 12.5276 1.33341 12.6667 1.3335Z"
                fill="#07092C"
              />
            </svg>{' '}
            Talk to Mini PT AI
          </button>
        </div>
      </div>

      <div id="builder-controls" className="builder-controls">
        <label id="checkbox-label" className="checkbox-label">
          {/* <input
            type="checkbox"
            checked={allWeeksSame}
            onChange={e => setAllWeeksSame(e.target.checked)}
          />
          <span className="checkbox-custom"></span> */}
          <Checkbox
            checked={allWeeksSame}
            onChange={(e) => {
              const checked = e.target.checked;
              setAllWeeksSame(checked);
              if (checked) {
                store.toggleWeek('All weeks', true);
              }
            }}
            className="all-week-same"
          />
          All weeks the same
        </label>
        <span className="placed-counter">
          <strong>{totalExercisesPlaced}</strong> exercises placed
        </span>
      </div>

      <div className="weeks-container">
        {(allWeeksSame ? [{ ...weeks[0], _id: 'All weeks' }] : weeks).map((week) => (
          <ProgramWeek key={week._id} store={store} week={week} onOpenLibrary={onOpenLibrary} />
        ))}
      </div>

      {/* AI Assistant Modal */}
      <Modal
        open={isAiAssistantOpen}
        onCancel={() => {
          setAiAssistantOpen(false);
          if (aiModifySuccess && setAiModifySuccess) {
            setAiModifySuccess(false);
          }
        }}
        footer={null}
        centered
        width={480}
        rootClassName="antd-remix"
      >
        <div className={`remix-modal-content ${isAIModifying ? `ai-program-generating` : ``}`}>
          <MiniPTAi src={miniPtCare} />

          {aiModifySuccess ? (
            <div className="remix-success-content" style={{ textAlign: 'center' }}>
              <h2
                className="mt-0"
                style={{
                  fontSize: '24px',
                  marginBottom: '24px',
                  fontWeight: 600,
                  lineHeight: `36px`,
                }}
              >
                The program has been updated with the new information
              </h2>
              <button
                style={{
                  background: '#fff',
                  color: '#000',
                  border: 'none',
                  padding: '12px 24px',
                  borderRadius: '12px',
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px',
                  fontSize: '16px',
                  fontWeight: 500,
                  transition: 'opacity 0.2s ease',
                  minHeight: 54,
                }}
                onClick={() => {
                  setAiAssistantOpen(false);
                  if (setAiModifySuccess) setAiModifySuccess(false);
                }}
                onMouseEnter={(e) => (e.currentTarget.style.opacity = '0.9')}
                onMouseLeave={(e) => (e.currentTarget.style.opacity = '1')}
              >
                <svg
                  width={13}
                  height={10}
                  viewBox="0 0 13 10"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M11.6667 5H1M1 5L5 9M1 5L5 1"
                    stroke="#07092C"
                    strokeWidth={2}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                See Program
              </button>
            </div>
          ) : isAIModifying ? (
            <div className="remix-modifying-content" style={{ textAlign: 'center' }}>
              <h2 style={{ fontSize: '24px', fontWeight: 600, color: '#fff', lineHeight: `36px` }}>
                {getAIText(selectedAIOption)}
              </h2>
            </div>
          ) : (
            <>
              <h2>How can I assist you today?</h2>
              <div className="remix-options-list">
                <button
                  className="remix-option-btn"
                  onClick={() =>
                    onAIModifyWorkout && onAIModifyWorkout('Gradually raise workout intensity')
                  }
                >
                  Gradually raise workout intensity
                </button>
                <button
                  className="remix-option-btn"
                  onClick={() => onAIModifyWorkout && onAIModifyWorkout('Adjust for gym equipment')}
                >
                  Adjust for gym equipment
                </button>
                <button
                  className="remix-option-btn"
                  onClick={() =>
                    onAIModifyWorkout && onAIModifyWorkout('Progressively increase weights or reps')
                  }
                >
                  Progressively increase weights or reps
                </button>
                <button
                  className="remix-option-btn"
                  onClick={() =>
                    onAIModifyWorkout && onAIModifyWorkout('Convert to superset format')
                  }
                >
                  Convert to superset format
                </button>
              </div>
            </>
          )}
        </div>
      </Modal>

      {/* Remix Modal placeholder - can be added here or in ProgramDaySlot if it needs to be different */}
      <Modal
        open={isRemixModalOpen}
        onCancel={() => setRemixModalOpen(false)}
        footer={null}
        centered
        width={480}
        rootClassName="antd-remix"
      >
        <h2 className="text-center">Remix</h2>
      </Modal>

      {/* Floating Library Trigger for Mobile */}

      {/* Floating Library Trigger for Mobile */}
      <button className="lib-trigger-btn" onClick={onOpenLibrary}>
        <svg
          width={20}
          height={20}
          viewBox="0 0 20 20"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M5 3.667C5 2.95967 5.28099 2.28131 5.78115 1.78115C6.28131 1.28099 6.95967 1 7.667 1H16.333C16.6832 1 17.03 1.06898 17.3536 1.20301C17.6772 1.33704 17.9712 1.53349 18.2189 1.78115C18.4665 2.0288 18.663 2.32281 18.797 2.64638C18.931 2.96996 19 3.31676 19 3.667V12.333C19 12.6832 18.931 13.03 18.797 13.3536C18.663 13.6772 18.4665 13.9712 18.2189 14.2189C17.9712 14.4665 17.6772 14.663 17.3536 14.797C17.03 14.931 16.6832 15 16.333 15H7.667C6.95967 15 6.28131 14.719 5.78115 14.2189C5.28099 13.7187 5 13.0403 5 12.333V3.667Z"
            stroke="#04051B"
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M2.012 5.26C1.705 5.43443 1.44965 5.68702 1.2719 5.99211C1.09415 6.2972 1.00034 6.64391 1 6.997V16.997C1 18.097 1.9 18.997 3 18.997H13C13.75 18.997 14.158 18.612 14.5 17.997M9 5H14M9 8H15M9 11H12"
            stroke="#04051B"
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>
    </main>
  );
};
